import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Package, AlertTriangle, TrendingDown, 
  Plus, Minus, Search
} from 'lucide-react';
import { useAdmin } from '../../context/AdminContext';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminInventory = () => {
  const { getAuthHeaders } = useAdmin();
  const [inventory, setInventory] = useState([]);
  const [alerts, setAlerts] = useState({ low_stock: [], out_of_stock: [] });
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [adjustment, setAdjustment] = useState({ amount: 0, reason: '' });

  useEffect(() => {
    fetchInventoryData();
  }, []);

  const fetchInventoryData = async () => {
    setLoading(true);
    try {
      const [inventoryRes, alertsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/admin/inventory?limit=50`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/inventory/alerts`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/inventory/summary`, { headers: getAuthHeaders() })
      ]);
      
      setInventory(inventoryRes.data.products || []);
      setAlerts(alertsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const openAdjustModal = (product) => {
    setSelectedProduct(product);
    setAdjustment({ amount: 0, reason: 'manual' });
    setShowAdjustModal(true);
  };

  const handleAdjustment = async () => {
    if (!selectedProduct || adjustment.amount === 0) return;
    
    const sku = selectedProduct.variants?.[0]?.sku;
    if (!sku) {
      console.error('No SKU found for product');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/inventory/adjust`,
        {
          sku: sku,
          adjustment: adjustment.amount,
          reason: adjustment.reason,
          notes: `Manual adjustment: ${adjustment.amount > 0 ? '+' : ''}${adjustment.amount}`
        },
        { headers: getAuthHeaders() }
      );
      setShowAdjustModal(false);
      fetchInventoryData();
    } catch (error) {
      console.error('Failed to adjust inventory:', error);
    }
  };

  const filteredInventory = inventory.filter(item =>
    item.name?.toLowerCase().includes(search.toLowerCase()) ||
    item.variants?.some(v => v.sku?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <AdminLayout>
      <div className="p-6" data-testid="admin-inventory-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-black text-stone-900">Inventory</h1>
          <p className="text-stone-500">Monitor and manage stock levels</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500">Total Products</p>
                      <p className="text-2xl font-black">{summary.total_products || 0}</p>
                    </div>
                    <Package className="h-8 w-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500">Total Stock</p>
                      <p className="text-2xl font-black">{summary.total_stock || 0}</p>
                    </div>
                    <Package className="h-8 w-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg bg-yellow-50">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-yellow-700">Low Stock</p>
                      <p className="text-2xl font-black text-yellow-800">{summary.low_stock_count || 0}</p>
                    </div>
                    <TrendingDown className="h-8 w-8 text-yellow-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg bg-red-50">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-red-700">Out of Stock</p>
                      <p className="text-2xl font-black text-red-800">{summary.out_of_stock_count || 0}</p>
                    </div>
                    <AlertTriangle className="h-8 w-8 text-red-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Alerts */}
            {(alerts.low_stock?.length > 0 || alerts.out_of_stock?.length > 0) && (
              <Card className="mb-8 border-0 shadow-lg border-l-4 border-l-amber-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-800">
                    <AlertTriangle className="h-5 w-5" />
                    Stock Alerts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {alerts.out_of_stock?.length > 0 && (
                      <div className="p-4 bg-red-50 rounded-lg">
                        <h4 className="font-bold text-red-800 mb-2">Out of Stock ({alerts.out_of_stock.length})</h4>
                        <ul className="space-y-1 text-sm text-red-700">
                          {alerts.out_of_stock.slice(0, 5).map(item => (
                            <li key={item._id}>• {item.name}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {alerts.low_stock?.length > 0 && (
                      <div className="p-4 bg-yellow-50 rounded-lg">
                        <h4 className="font-bold text-yellow-800 mb-2">Low Stock ({alerts.low_stock.length})</h4>
                        <ul className="space-y-1 text-sm text-yellow-700">
                          {alerts.low_stock.slice(0, 5).map(item => (
                            <li key={item._id}>• {item.name} ({item.stock} left)</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Search */}
            <Card className="mb-6 border-0 shadow-lg">
              <CardContent className="p-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    placeholder="Search by product name or SKU..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="inventory-search"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Inventory Table */}
            <Card className="border-0 shadow-lg" data-testid="inventory-table">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">Product</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">SKU</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">Category</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">Stock</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">Status</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-stone-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredInventory.map((item) => (
                        <tr key={item._id} className="hover:bg-stone-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              {item.images?.[0] ? (
                                <img src={item.images[0]} alt="" className="w-10 h-10 object-cover rounded" />
                              ) : (
                                <div className="w-10 h-10 bg-stone-100 rounded flex items-center justify-center">
                                  <Package className="h-5 w-5 text-stone-400" />
                                </div>
                              )}
                              <span className="font-medium text-stone-900">{item.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-stone-600 text-sm font-mono">
                            {item.variants?.[0]?.sku || '-'}
                          </td>
                          <td className="px-6 py-4 text-stone-600">{item.category}</td>
                          <td className="px-6 py-4 font-bold">{item.stock}</td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              item.stock === 0 
                                ? 'bg-red-100 text-red-700'
                                : item.stock <= 5
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-green-100 text-green-700'
                            }`}>
                              {item.stock === 0 ? 'Out of Stock' : item.stock <= 5 ? 'Low Stock' : 'In Stock'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openAdjustModal(item)}
                              data-testid={`adjust-${item._id}`}
                            >
                              Adjust
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Adjustment Modal */}
            <Dialog open={showAdjustModal} onOpenChange={setShowAdjustModal}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Adjust Inventory</DialogTitle>
                </DialogHeader>
                
                {selectedProduct && (
                  <div className="space-y-4">
                    <div className="p-4 bg-stone-50 rounded-lg">
                      <p className="font-bold">{selectedProduct.name}</p>
                      <p className="text-sm text-stone-500">Current Stock: {selectedProduct.stock}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Adjustment Amount</label>
                      <div className="flex items-center gap-4">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setAdjustment(a => ({...a, amount: a.amount - 1}))}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <Input
                          type="number"
                          value={adjustment.amount}
                          onChange={(e) => setAdjustment(a => ({...a, amount: parseInt(e.target.value) || 0}))}
                          className="w-24 text-center"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setAdjustment(a => ({...a, amount: a.amount + 1}))}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-sm text-stone-500 mt-2">
                        New stock will be: {selectedProduct.stock + adjustment.amount}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Reason</label>
                      <select
                        value={adjustment.reason}
                        onChange={(e) => setAdjustment(a => ({...a, reason: e.target.value}))}
                        className="w-full p-2 border rounded"
                      >
                        <option value="manual">Manual Adjustment</option>
                        <option value="restock">Restock</option>
                        <option value="damage">Damage/Loss</option>
                        <option value="return">Return</option>
                        <option value="correction">Correction</option>
                      </select>
                    </div>
                  </div>
                )}

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAdjustModal(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleAdjustment}
                    disabled={adjustment.amount === 0}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    Apply Adjustment
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminInventory;
